"use client";

import { defaultVertex, transition } from "@/mono/lib/gl/shaderChunks";
import useAnimation from "@/mono/lib/hooks/useAnimation";
import usePromisifyValue from "@/mono/lib/hooks/usePromisifyValue";
import { Text, useTexture } from "@react-three/drei";

import trans from "@/assets/transition_text.png";
import { wait } from "@/mono/lib/common";
import { EASE_CUSTOM_2 } from "@/mono/lib/styles/easing";
import { useFrame } from "@react-three/fiber";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Color, RepeatWrapping } from "three";
import { EASE_CUSTOM_3 } from "mono/lib/styles/easing";

const _fragmentShader = /*glsl*/ `
    varying vec2 vUv;
    uniform float uTime;
    uniform float uAlpha;
    uniform float uTransition;
    uniform sampler2D tTransition;
    uniform vec3 uColor;

    ${transition}    

    void main() {
      
      vec2 uv = vUv;
      uv.x *= 10.;
      uv.y *= 2.;
      vec3 color = uColor;

      vec4 transition = texture2D(tTransition, uv);

      if (uTransition < 0.01) {
        discard;
      }

      // float t = falloffsmooth(uTransition, 0., 1., 0.1, transition.r);
      //float t = falloffsmooth(transition.r, 0., 1., 1., uTransition);

      // float a = uAlpha * uTransition;
      gl_FragColor = vec4(color, uTransition);
      //gl_FragColor = vec4(transition.rgb, 1.);
    }
`;

function GLText(
  {
    fragmentShader = _fragmentShader,
    uniforms = {},
    color = "#FFFFFF",
    children,
    weight = "Bold",
    material = {},
    immediate = false,
    ...props
  },
  ref
) {
  const localRef = useRef();
  const visible = useRef(false);

  const weightMap = {
    Bold: "/fonts/RestartSoft-Bold.otf",
    Medium: "/fonts/RestartSoft-Medium.otf",
    Light: "/fonts/RestartSoft-Light.otf",
    Mono: "/fonts/IBMPlexMono-Light.ttf",
  };

  const [_, isSynced, setSyncState] = usePromisifyValue({ synced: false });

  const tTransition = useTexture(trans.src);
  tTransition.wrapS = tTransition.wrapT = RepeatWrapping;

  const shader = useMemo(
    () => ({
      transparent: true,
      uniforms: {
        uTime: { value: 0 },
        uTransition: { value: immediate ? 1 : 0 },
        tTransition: { value: tTransition },
        uAlpha: { value: 1 },
        uColor: {
          value:
            typeof color === "string"
              ? new Color(color).convertLinearToSRGB()
              : color,
        },
        ...uniforms,
      },
      vertexShader: defaultVertex,
      fragmentShader,
      ...material,
    }),
    []
  );

  const duration = 0.4;

  const { animateIn, animateOut } = useAnimation({
    inParams: {
      duration,

      ease: EASE_CUSTOM_3,
      onStart: async () => {
        await wait(50);
        visible.current = true;
        localRef.current.visible = true;
      },
      shouldCancel: () => visible.current,
      onUpdate: (v) => {
        shader.uniforms.uTransition.value = v;
      },
    },
    outParams: {
      ease: EASE_CUSTOM_3,
      duration,
      onStart: async () => {
        await wait(50);
        visible.current = false;
      },
      shouldCancel: () => !visible.current,
      onComplete: () => {
        localRef.current.visible = false;
      },
      onUpdate: (v) => {
        shader.uniforms.uTransition.value = v;
      },
    },
  });

  function handleSync() {
    setSyncState({ synced: true });
  }

  useEffect(() => {
    localRef.current.addEventListener("synccomplete", handleSync);

    return () => {
      if (localRef.current)
        localRef.current.removeEventListener("synccomplete", handleSync);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    mesh: localRef.current,
    animateIn: (params) => {
      animateIn(params);
    },
    animateOut,
    isSynced,
    getBoundingBox: () => {
      localRef.current.geometry.computeBoundingBox();
      return localRef.current.geometry.boundingBox;
    },
  }));

  useFrame(({ clock }) => {
    shader.uniforms.uTime.value = clock.getElapsedTime();
  });

  return (
    <Text font={weightMap[weight]} ref={localRef} {...props}>
      <shaderMaterial args={[shader]} />
      {children}
    </Text>
  );
}

export default forwardRef(GLText);
